import {
  VisitData,
  VisitStatus as VisitStatusEnum,
  VisitStatusType,
} from "./types";

/**
 * Shared utility functions for visit list components
 * Used by both Kinsa and Owlet implementations
 */

/**
 * Formats timestamp based on visit status and app preferences
 */
export const formatVisitTimestamp = (
  timestamp: string | Date,
  status: VisitStatusType,
  format: "time" | "relative" | "date" = "time"
): string => {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;

  // For requested visits, always show time
  if (status === VisitStatusEnum.REQUESTED) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }

  // For completed and cancelled visits with date format (Owlet style)
  if (
    (status === VisitStatusEnum.COMPLETED ||
      status === VisitStatusEnum.CANCELLED) &&
    format === "date"
  ) {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  }

  // For relative time format (Kinsa style)
  if (format === "relative") {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  // Default: time format
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

/**
 * Determines avatar state based on visit status and clinician assignment
 */
export const getVisitAvatarState = (
  status: VisitStatusType,
  hasClinician: boolean
): "assigned" | "not-assigned" => {
  if (status === VisitStatusEnum.REQUESTED || !hasClinician) {
    return "not-assigned";
  }
  return "assigned";
};

/**
 * Determines if a visit should be clickable based on status
 */
export const getVisitClickability = (status: VisitStatusType): boolean => {
  return (
    status === VisitStatusEnum.REQUESTED ||
    status === VisitStatusEnum.ACTIVE ||
    status === VisitStatusEnum.COMPLETED
  );
};

/**
 * Generates visit title based on status and patient/clinician info
 */
export const generateVisitTitle = (visit: {
  status: VisitStatusType;
  patientName?: string;
  patientFirstName?: string;
  patientLastName?: string;
  clinicianName?: string;
  clinicianFirstName?: string;
  clinicianLastName?: string;
}): string => {
  const patientFirstName = visit.patientFirstName || "";
  const patientName = visit.patientName || patientFirstName || "Patient";
  const clinicianName = visit.clinicianFirstName
    ? `Dr. ${visit.clinicianFirstName} ${visit.clinicianLastName || ""}`.trim()
    : visit.clinicianName || "";

  switch (visit.status) {
    case VisitStatusEnum.REQUESTED: {
      const fullPatientName =
        visit.patientFirstName && visit.patientLastName
          ? `${visit.patientFirstName} ${visit.patientLastName}`.trim()
          : patientName;
      return fullPatientName
        ? `Reviewing ${fullPatientName}'s details...`
        : "Reviewing Patient Details";
    }
    case VisitStatusEnum.ACTIVE: {
      if (patientName && clinicianName) {
        return `${patientName} & ${clinicianName}`;
      }
      return patientName ? `${patientName}'s Visit` : "Patient Visit";
    }
    case VisitStatusEnum.COMPLETED: {
      if (patientName && clinicianName) {
        return `${patientName} & ${clinicianName}`;
      }
      return patientName || "Visit Summary";
    }
    case VisitStatusEnum.CANCELLED: {
      const fullPatientName =
        visit.patientFirstName && visit.patientLastName
          ? `${visit.patientFirstName} ${visit.patientLastName}`.trim()
          : patientName;
      return fullPatientName ? `${fullPatientName}'s Visit` : "Cancelled Visit";
    }
    default: {
      return patientName ? `${patientName}'s Visit` : "Patient Visit";
    }
  }
};

/**
 * Generates visit subtitle based on status
 */
export const generateVisitSubtitle = (
  status: VisitStatusType,
  lastMessage?: string,
  customSubtitles?: Partial<Record<VisitStatusType, string>>
): string => {
  const defaultSubtitles: Record<VisitStatusType, string> = {
    [VisitStatusEnum.REQUESTED]: "Responses in under an hour",
    [VisitStatusEnum.ACTIVE]: "Your visit details",
    [VisitStatusEnum.COMPLETED]: "Visit summary",
    [VisitStatusEnum.CANCELLED]: "Cancelled Visit",
    ...customSubtitles,
  };

  return defaultSubtitles[status] || lastMessage || "No recent activity";
};

/**
 * Determines if visit should show notification card
 */
export const shouldShowNotificationCard = (
  visit: VisitData,
  showNotificationCard: boolean = false
): boolean => {
  if (!showNotificationCard) return false;

  return (
    visit.status === VisitStatusEnum.REQUESTED ||
    (visit.status === VisitStatusEnum.ACTIVE && !!visit.hasUnreadMessages)
  );
};

/**
 * Gets notification message for visit
 */
export const getNotificationMessage = (visit: VisitData): string | null => {
  switch (visit.status) {
    case VisitStatusEnum.REQUESTED:
      return "You can close the app, we will notify you as soon as the doctor has responded";
      break;
  }
  return null;
};
